import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { RefreshZoneRequestDTO } from '@/core/api/dtos/request/zone/refresh'
import { Zone } from '@/core/api/entities/zone/zone'
import { useRefreshZone } from '@/core/hooks/useZone'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface UseZoneRefreshProps {
	onSuccess?: () => void
	onError?: (error: Error) => void
}

export const useZoneRefresh = (props?: UseZoneRefreshProps) => {
	const { onSuccess, onError } = props || {}
	const { showNotification } = useNotificationContext()
	const { t } = useTranslation('common')
	const [selectedZone, setSelectedZone] = useState<Zone | null>(null)
	const [isModalVisible, setIsModalVisible] = useState(false)

	const { mutate, isPending } = useRefreshZone({
		options: {
			onSuccess: () => {
				if (selectedZone?.uuid) {
					// Show success notification
					showNotification({
						type: 'success',
						message: t('entities.zone.refresh.success.title'),
						description: t('entities.zone.refresh.success.description', {
							name: selectedZone.name,
						}),
					})

					// Close modal
					setIsModalVisible(false)
					setSelectedZone(null)

					// Call onSuccess callback if provided
					if (onSuccess) onSuccess()
				}
			},
			onError: (error: Error) => {
				// Show error notification
				showNotification({
					type: 'error',
					message: t('entities.zone.refresh.error.title'),
					description: t('entities.zone.refresh.error.description', {
						name: selectedZone?.name,
					}),
				})

				// Call onError callback if provided
				if (onError) onError(error)
			},
		},
	})

	const openRefreshModal = (zone: Zone) => {
		setSelectedZone(zone)
		setIsModalVisible(true)
	}

	const closeRefreshModal = () => {
		setIsModalVisible(false)
		setSelectedZone(null)
	}

	const handleRefreshZone = (data: RefreshZoneRequestDTO) => {
		if (selectedZone?.uuid) {
			mutate({
				zoneUUID: selectedZone.uuid,
				data,
			})
		}
	}

	return {
		selectedZone,
		isModalVisible,
		isRefreshing: isPending,
		openRefreshModal,
		closeRefreshModal,
		handleRefreshZone,
	}
}

export default useZoneRefresh
